import 'react-data-grid/lib/styles.css'
import './data-grid.css'
import { type ComponentProps } from 'react'
import { Column, default as ReactDataGrid } from 'react-data-grid'
import { datagrid, hidden, sortableHeaderCellContainer, sortableHeaderCellIcon } from './data-grid.module.css'
import clsx from 'clsx'
import { ChevronDown, ChevronsUpDown, ChevronUp } from 'lucide-react'
import { DefaultDataGridCells, FileCell, RendererMap, RenderProps } from './types'
import { CellRenderer } from './renderers'

type Props<R, SR = unknown, K extends React.Key = React.Key> = {
  className?: string
  renderers: RendererMap
  theme?: 'light' | 'dark'
  editable?: boolean
  onFileUpload: RenderProps<FileCell>['uploadFile']
  columns: (Column<R, SR> & { cell: Omit<DefaultDataGridCells, 'value'> })[]
} & Omit<ComponentProps<typeof ReactDataGrid<R, SR, K>>, 'renderers' | 'columns'>

export const DataGrid = <R, SR = unknown, K extends React.Key = React.Key>({
  className,
  columns,
  onFileUpload,
  editable = true,
  ...props
}: Props<R, SR, K>) => {
  const augmentedColumns: readonly Column<any, any>[] = columns.map((column) => {
    return {
      ...column,
      editable: (column.editable && editable) ?? false,
      renderCell: (props) => {
        return (
          <CellRenderer
            mode="view"
            {...props}
            uploadFile={onFileUpload}
            cell={
              {
                ...column.cell,
                value: props.row[column.key],
              } as DefaultDataGridCells
            }
            onClose={() => {}}
          />
        )
      },
      renderEditCell: (props) => {
        return (
          <CellRenderer
            mode="edit"
            uploadFile={onFileUpload}
            {...props}
            cell={{ ...column.cell, value: props.row[column.key] } as DefaultDataGridCells}
          />
        )
      },
      renderHeaderCell: (props) => {
        const { column: ccolumn, sortDirection } = props
        const Icon = sortableHeaderCellIcons[sortDirection ?? 'NONE']
        return (
          <div className={clsx(ccolumn.sortable && sortableHeaderCellContainer, 'flex items-center justify-between')}>
            <p className="font-semibold text-xs">{props.column.name}</p>
            <Icon size={16} className={clsx(sortableHeaderCellIcon, !sortDirection && hidden)} />
          </div>
        )
      },
    }
  })
  return (
    <ReactDataGrid<R, SR, K>
      {...props}
      className={clsx(datagrid, 'bp-data-grid text-xs', className)}
      columns={augmentedColumns}
    />
  )
}

const sortableHeaderCellIcons = {
  ASC: ChevronDown,
  DESC: ChevronUp,
  NONE: ChevronsUpDown,
}

DataGrid.displayName = 'DataGrid'
